import React, { useState, useEffect, useRef, useCallback, createContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { api, createSession, configureAuth, refreshAccessTokenManually } from '../services/api';
import { changeLanguage } from '../i18n';
import { toast } from "react-toastify";
import { getPermissionsForRole, hasModuleAccess } from '../constants/permissions';
import { setEmpresaCurrency, clearEmpresaCurrency } from '../utils/currency';

export const AuthContext = createContext();

// URL do backend para chamadas diretas (refresh não usa a instância api para evitar loop)
const hostname = window.location.hostname;
const isDevelopment =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.includes("ngrok") ||
    process.env.REACT_APP_FORCE_DEV === "true";

const apiUrl = (isDevelopment
    ? process.env.REACT_APP_SERVER_URL_DEV
    : process.env.REACT_APP_SERVER_URL_PROD
)?.replace(/\/+$/, ''); // Remover trailing slash para evitar URL com //

export const AuthProvider = ({ children }) => {
    const { t } = useTranslation('auth');
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasLicense, setHasLicense] = useState(null);
    const [redirectToPlanos, setRedirectToPlanos] = useState(false);

    // Access token em memória (NÃO no localStorage)
    const accessTokenRef = useRef(null);

    const getAccessToken = useCallback(() => accessTokenRef.current, []);
    const setAccessToken = useCallback((token) => { accessTokenRef.current = token; }, []);

    // Logout: invalidar refresh token no servidor + limpar state
    const logout = useCallback(async () => {
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log("[AUTH] logout");
        try {
            await axios.post(`${apiUrl}/api/v1/auth/logout`, {}, { withCredentials: true });
        } catch (_) { /* ignore - pode falhar se já desconectado */ }

        accessTokenRef.current = null;
        setUser(null);
        setHasLicense(false);
        localStorage.removeItem("user");
        localStorage.removeItem("token"); // Limpar legado se existir
        localStorage.removeItem("empresa");
        // Sem isto a próxima empresa a logar nesta aba herdaria a moeda da anterior.
        clearEmpresaCurrency();
        navigate("/login");
    }, [navigate]);

    // Configurar bridge entre api.js interceptors e AuthContext
    useEffect(() => {
        configureAuth({
            getAccessToken: () => accessTokenRef.current,
            setAccessToken: (t) => { accessTokenRef.current = t; },
            logout,
        });
        // Expor getter global para utilitários que não são React (ex: logger.js)
        window.__getAccessToken = () => accessTokenRef.current;
    }, [logout]);

    // F5 RESILIENCE: ao montar, tenta restaurar sessão via refresh cookie
    useEffect(() => {
        const initAuth = async () => {
            // 🔒 SKIP AUTH em rotas públicas do cardápio - não precisam de autenticação
            const currentPath = window.location.pathname;
            const isPublicRoute = currentPath.startsWith('/cardapio') ||
                                  currentPath.startsWith('/c/') ||
                                  currentPath.startsWith('/cardapio-salao');

            if (isPublicRoute) {
                console.log('[AUTH] Rota pública detectada - pulando autenticação:', currentPath);
                setLoading(false);
                // Remove o loading HTML imediatamente para rotas públicas
                if (window.__removeInitialLoading) {
                    window.__removeInitialLoading();
                }
                return;
            }

            try {
                const response = await axios.post(
                    `${apiUrl}/api/v1/auth/refresh`,
                    {},
                    { withCredentials: true }
                );

                if (response.status === 200 && response.data.token) {
                    accessTokenRef.current = response.data.token;
                    setUser(response.data.user);
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                    localStorage.setItem('empresa', JSON.stringify(response.data.empresa));
                    setEmpresaCurrency(response.data.empresa);

                    // Restaurar idioma preferido da empresa
                    if (response.data.empresa?.preferredLanguage) {
                        changeLanguage(response.data.empresa.preferredLanguage);
                    }

                    console.log('[AUTH] Sessão restaurada via refresh cookie');
                } else {
                    throw new Error('Refresh retornou status inesperado');
                }
            } catch {
                // Cookie expirado ou inexistente → verificar token legado no localStorage
                const legacyToken = localStorage.getItem('token');
                if (legacyToken) {
                    // Período de migração: aceitar token antigo do localStorage
                    console.log('[AUTH] Usando token legado do localStorage (período de migração)');
                    accessTokenRef.current = legacyToken;

                    const storedEmpresa = localStorage.getItem('empresa');
                    if (storedEmpresa) {
                        try {
                            const empresaData = JSON.parse(storedEmpresa);
                            if (empresaData.preferredLanguage) {
                                changeLanguage(empresaData.preferredLanguage);
                            }
                        } catch (_) { }
                    }

                    // Verificar se o token legado ainda é válido com uma requisição leve
                    try {
                        const testResponse = await axios.get(`${apiUrl}/api/v1/empresa/vinculo`, {
                            headers: { 'Authorization': `Bearer ${legacyToken}` },
                            validateStatus: () => true,
                        });
                        if (testResponse.status === 401 || testResponse.status === 403) {
                            // Token legado inválido → limpar tudo
                            accessTokenRef.current = null;
                            localStorage.removeItem("user");
                            localStorage.removeItem("token");
                            localStorage.removeItem("empresa");
                        } else if (testResponse.status === 200 && testResponse.data?.vinculo) {
                            // Token legado válido → atualizar user a partir da resposta
                            const vinculo = testResponse.data.vinculo;
                            localStorage.setItem('empresa', JSON.stringify(vinculo));
                            setEmpresaCurrency(vinculo);
                        }
                    } catch (_) {
                        // Em caso de erro de rede, manter o token legado
                    }

                    // Tentar recuperar user do localStorage encriptado (compatibilidade)
                    if (accessTokenRef.current) {
                        try {
                            const userEncrypted = localStorage.getItem('user');
                            // Validar que é uma string CryptoJS válida (começa com "U2FsdGVkX1" = base64 de "Salted__")
                            if (userEncrypted && typeof userEncrypted === 'string' && userEncrypted.startsWith('U2FsdGVkX1')) {
                                const CryptoJS = (await import('crypto-js')).default;
                                const secretKey = 'my-custom-secret-key:1af0addcce4b56f753ed10c285667ad031bae57af19747f576b05312b8cf85be';
                                const decrypted = CryptoJS.AES.decrypt(userEncrypted, secretKey).toString(CryptoJS.enc.Utf8);
                                if (decrypted) {
                                    setUser(JSON.parse(decrypted));
                                }
                            } else {
                                // Dado não encriptado ou corrompido → limpar
                                localStorage.removeItem('user');
                            }
                        } catch (_) {
                            // Falha ao decriptar = limpar dados corrompidos
                            localStorage.removeItem('user');
                            accessTokenRef.current = null;
                        }
                    }
                } else {
                    // Sem cookie e sem token legado → não autenticado
                    accessTokenRef.current = null;
                    setUser(null);
                    localStorage.removeItem("user");
                    localStorage.removeItem("empresa");
                }
            } finally {
                setLoading(false);
                // Remove o loading HTML para rotas que não passam por PrivateRoute (login, cardápio, etc)
                if (window.__removeInitialLoading) {
                    window.__removeInitialLoading();
                }
            }
        };

        initAuth();
    }, []);

    // PROACTIVE TOKEN REFRESH: Renova token periodicamente ANTES de expirar
    // Resolve o problema de logout na tela de pedidos que usa apenas WebSocket (sem chamadas HTTP)
    // Access token expira em 15min, então renovamos a cada 12min para nunca expirar
    useEffect(() => {
        if (!user) return;

        const REFRESH_INTERVAL = 12 * 60 * 1000; // 12 minutos (token expira em 15)

        const intervalId = setInterval(async () => {
            console.log('[AUTH] Renovação proativa de token (timer de 12min)...');
            try {
                const newToken = await refreshAccessTokenManually();
                if (newToken) {
                    console.log('[AUTH] Token renovado proativamente com sucesso');
                } else {
                    console.warn('[AUTH] Renovação proativa falhou (null) - mantendo sessão, tentará novamente no próximo ciclo');
                    // NÃO fazer logout aqui - o interceptor de 401 vai lidar quando necessário
                }
            } catch {
                console.warn('[AUTH] Erro na renovação proativa - mantendo sessão');
                // NÃO fazer logout - falha transiente não deve derrubar a sessão
            }
        }, REFRESH_INTERVAL);

        return () => clearInterval(intervalId);
    }, [user]);

    // VISIBILITYCHANGE: renovar token quando aba volta do background
    // Resolve o problema de 401 em cascata após ficar muito tempo sem usar
    useEffect(() => {
        if (!user) return; // Só monitora se estiver autenticado

        let lastHiddenAt = null;
        const MIN_HIDDEN_MS = 5 * 60 * 1000; // 5 minutos - renovar se ficou mais que isso em background

        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'hidden') {
                lastHiddenAt = Date.now();
                return;
            }

            // Aba ficou visível novamente
            if (document.visibilityState === 'visible' && lastHiddenAt) {
                const hiddenDuration = Date.now() - lastHiddenAt;
                lastHiddenAt = null;

                // Se ficou em background por mais de 5 minutos, renovar token proativamente
                if (hiddenDuration >= MIN_HIDDEN_MS) {
                    console.log(`[AUTH] Aba voltou do background após ${Math.round(hiddenDuration / 60000)}min - renovando token...`);

                    // Aguardar rede estabilizar após suspend (Wi-Fi pode demorar a reconectar)
                    await new Promise(r => setTimeout(r, 2000));

                    // Tentar até 3 vezes antes de desistir (rede pode estar instável após wake)
                    let refreshed = false;
                    for (let attempt = 1; attempt <= 3; attempt++) {
                        try {
                            const newToken = await refreshAccessTokenManually();
                            if (newToken) {
                                console.log(`[AUTH] Token renovado com sucesso (tentativa ${attempt})`);
                                refreshed = true;
                                break;
                            }
                        } catch {
                            console.warn(`[AUTH] Tentativa ${attempt}/3 de refresh falhou`);
                        }
                        // Aguardar entre tentativas (2s, 4s)
                        if (attempt < 3) {
                            await new Promise(r => setTimeout(r, 2000 * attempt));
                        }
                    }

                    if (!refreshed) {
                        console.warn('[AUTH] Todas as 3 tentativas de refresh falharam após retorno do background');
                        // Não fazer logout imediato - o interceptor vai lidar quando o usuário fizer uma ação
                        // Só logar se ficou em background por mais de 1 hora (provavelmente sessão real expirou)
                        if (hiddenDuration > 60 * 60 * 1000) {
                            console.warn('[AUTH] Sessão possivelmente expirada (>1h em background) - fazendo logout');
                            logout();
                        }
                    }
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [user, logout]);

    // Login
    const login = async (email, password) => {
        try {
            const response = await createSession(email, password);
            if (response.status === 200) {
                toast(response.data.msg || t('toasts.loginSuccess'), { type: "success", autoClose: 2000 });

                const loggedUser = response.data.user;
                const token = response.data.token;
                const empresa = response.data.empresa;

                // Access token em memória (NÃO no localStorage)
                accessTokenRef.current = token;

                // User e empresa no localStorage para dados de UI
                localStorage.setItem("user", JSON.stringify(loggedUser));
                localStorage.setItem("empresa", JSON.stringify(empresa));
                setEmpresaCurrency(empresa);

                // Aplicar idioma preferido da empresa
                if (empresa.preferredLanguage) {
                    changeLanguage(empresa.preferredLanguage);
                }

                setUser(loggedUser);
                navigate("/");
            } else {
                toast(response.msg || t('toasts.loginError'), { type: "error" });
            }
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log("error", error);
            toast(error?.response?.data?.msg || t('toasts.connectionError'), { type: "error" });
        }
    };

    // RBAC: verificar se o usuário possui uma permissão específica
    const userHasPermission = useCallback((permission) => {
        const perms = getPermissionsForRole(user?.role);
        if (perms.includes('*')) return true;
        return perms.includes(permission);
    }, [user?.role]);

    // RBAC modular: verificar se o usuario tem acesso a um modulo especifico
    const userHasModule = useCallback((moduleName, level = 'read') => {
        if (!user) return false;
        return hasModuleAccess(user, moduleName, level);
    }, [user]);

    return (
        <AuthContext.Provider value={{
            authenticated: !!user,
            user,
            loading,
            hasLicense,
            redirectToPlanos,
            setRedirectToPlanos,
            setHasLicense,
            getAccessToken,
            setAccessToken,
            login,
            logout,
            userHasPermission,
            userHasModule
        }}>
            {children}
        </AuthContext.Provider>
    );
};
